// src/components/ContactForm/ContactForm.tsx
import React, { useState, FormEvent } from 'react';
import './ContactForm.css';

interface FormData {
  name: string;
  email: string;
  phone: string;
  questions: string;
}

interface FormMessage {
  type: 'success' | 'error';
  title: string;
  message: string;
}

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    questions: ''
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<FormMessage | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setMessage(null);

    try {
      console.log('Submitting form data:', formData);
      
      // CORRECTED: Use the right API ID and endpoint path
      const response = await fetch(process.env.REACT_APP_CONTACT_API || '', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      let result;
      try {
        const responseText = await response.text();
        console.log('Raw response:', responseText);
        
        if (responseText) {
          result = JSON.parse(responseText);
        } else {
          throw new Error('Empty response from server');
        }
      } catch (jsonError) {
        console.error('JSON parse error:', jsonError);
        throw new Error('Invalid response from server');
      }

      if (response.ok && result.message) {
        setMessage({
          type: 'success',
          title: '✨ Message Sent Successfully!',
          message: 'Thank you for reaching out! I\'ll respond to your cosmic inquiry within 24 hours. Check your email for a confirmation.'
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          questions: ''
        });
      } else {
        throw new Error(result.error || `Server error: ${response.status} - ${response.statusText}`);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      
      let errorMessage = 'There was an error sending your message. Please try again or contact us directly at astrologerkavitha@gmail.com';
      
      if (error instanceof Error) {
        if (error.message.includes('fetch') || error.message.includes('network')) {
          errorMessage = 'Network error. Please check your internet connection and try again.';
        } else if (error.message.includes('CORS')) {
          errorMessage = 'Connection error. Please try refreshing the page and trying again.';
        } else if (error.message.includes('404')) {
          errorMessage = 'Service temporarily unavailable. Please contact astrologerkavitha@gmail.com directly.';
        }
      }
      
      setMessage({
        type: 'error',
        title: '⚠️ Message Failed to Send',
        message: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact-form-section">
      <div className="container">
        {message && (
          <div className={`form-message ${message.type}`}>
            <h4>{message.title}</h4>
            <p>{message.message}</p>
          </div>
        )}
        
        <div className="contact-form">
          <h2>Get In Touch</h2>
          <p>Ready to explore your cosmic potential? Send me a message and let's begin your astrological journey together.</p>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={errors.name ? 'error' : ''}
                required
              />
              {errors.name && <div className="error-message">{errors.name}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={errors.email ? 'error' : ''}
                required
              />
              {errors.email && <div className="error-message">{errors.email}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number (Optional)</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="For appointment confirmation"
              />
            </div>

            <div className="form-group">
              <label htmlFor="questions">Your Questions & Focus Areas (Optional)</label>
              <textarea
                id="questions"
                name="questions"
                rows={5}
                value={formData.questions}
                onChange={handleInputChange}
                placeholder="What cosmic wisdom are you seeking? What areas of life would you like to explore? Any specific questions or concerns?"
              />
            </div>

            <button 
              type="submit" 
              className="cta-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
          
          {/* Debug info - remove in production */}
          {process.env.NODE_ENV === 'development' && (
            <div style={{ 
              marginTop: '2rem', 
              padding: '1rem', 
              background: 'rgba(255,255,255,0.1)', 
              borderRadius: '5px', 
              fontSize: '0.8rem',
              fontFamily: 'monospace'
            }}>
              <p><strong>Debug Info:</strong></p>
              <p>✅ Endpoint: {process.env.REACT_APP_CONTACT_API}</p>
              <p>Current domain: {window.location.hostname}</p>
              <p>API Gateway Type: HTTP API (v2)</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ContactForm;